/**
 * Executive Briefing dashboard mode.
 * Headline narrative + clickable insights/actions over live procurement data.
 *
 * Bullets and actions carry a `route` (validated against an allow-list on the
 * backend) so each item can deep-link straight into the relevant module.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { Sparkles, RefreshCw, AlertCircle, ArrowRight, Clock } from 'lucide-react';
import { Button } from '../../components/ui/button';

const fmtShort = (v) => v >= 1e6 ? `$${(v / 1e6).toFixed(1)}M` : v >= 1e3 ? `$${(v / 1e3).toFixed(0)}k` : `$${v || 0}`;

export default function AIBriefing({ initialData = null }) {
  const navigate = useNavigate();
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(!initialData);
  const [error, setError] = useState(null);

  const load = useCallback(async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.get(`/api/dashboard/ai-briefing${force ? '?force=true' : ''}`);
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Unable to generate briefing right now.');
    }
    setLoading(false);
  }, []);

  // If a prefetched payload arrives after first render (parent finished loading
  // it while we were already mounted), adopt it.
  useEffect(() => {
    if (initialData && !data) {
      setData(initialData);
      setLoading(false);
    }
  }, [initialData, data]);

  // Only fetch on mount when no prefetched data was provided.
  useEffect(() => {
    if (!initialData) load(false);
  }, [initialData, load]);

  const goto = (route) => { if (route) navigate(route); };
  const snap = data?.snapshot || {};

  return (
    <div className="space-y-5" data-testid="briefing">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-violet-600 via-fuchsia-600 to-rose-600 text-white p-8 shadow-xl">
        <div className="absolute -right-10 -top-10 opacity-10">
          <Sparkles className="h-64 w-64" strokeWidth={1} />
        </div>
        <div className="absolute top-4 right-4 flex items-center gap-2 text-[10px] uppercase tracking-wider opacity-80">
          {data?.cached && <div className="rounded-full bg-white/15 px-2.5 py-1 backdrop-blur">Cached</div>}
        </div>

        <div className="relative max-w-4xl">
          <div className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-[10px] font-medium uppercase tracking-wider backdrop-blur">
            <Sparkles className="h-3 w-3" /> Executive Briefing
          </div>
          {loading && !data ? (
            <div className="mt-4 space-y-3">
              <div className="h-7 w-3/4 animate-pulse rounded-full bg-white/20" />
              <div className="h-7 w-2/3 animate-pulse rounded-full bg-white/20" />
            </div>
          ) : error ? (
            <div className="mt-4 flex items-start gap-3 rounded-xl bg-white/10 p-4">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <div className="flex-1 text-sm opacity-90">{error}</div>
              <Button size="sm" variant="secondary" onClick={() => load(true)} data-testid="briefing-retry">Retry</Button>
            </div>
          ) : (
            <h1 className="mt-4 text-2xl md:text-3xl font-bold leading-tight">{data?.headline || ''}</h1>
          )}

          {data?.generated_at && !error && (
            <div className="mt-4 flex items-center gap-2 text-[11px] opacity-75">
              <Clock className="h-3 w-3" />
              <span>Generated {new Date(data.generated_at).toLocaleString()}</span>
              <button onClick={() => load(true)} className="ml-3 inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-1 hover:bg-white/25 transition-colors backdrop-blur" data-testid="briefing-refresh">
                <RefreshCw className={`h-3 w-3 ${loading ? 'animate-spin' : ''}`} /> Regenerate
              </button>
            </div>
          )}
        </div>

        {/* Snapshot tiles */}
        {data && !error && (
          <div className="relative mt-6 grid grid-cols-2 md:grid-cols-4 gap-3 max-w-4xl">
            <SnapshotTile label="Spend (12m)" value={fmtShort(snap.spend_12m || 0)} sub={`${snap.orders_12m || 0} POs`} onClick={() => navigate('/orders')} />
            <SnapshotTile label="Open RFx" value={snap.rfx_open || 0} sub="In flight" onClick={() => navigate('/rfq')} />
            <SnapshotTile label="AP overdue" value={fmtShort(snap.ap_overdue || 0)} sub="Action needed" onClick={() => navigate('/invoices')} />
            <SnapshotTile label="Contracts at risk" value={snap.contracts_expiring_90 || 0} sub="≤ 90 days" onClick={() => navigate('/data/contracts')} />
          </div>
        )}
      </div>

      {/* Bullets + Actions */}
      {data && !error && (
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
          <div className="lg:col-span-3 rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-zinc-100 px-5 py-3 bg-gradient-to-r from-violet-50 to-fuchsia-50">
              <div className="text-sm font-semibold text-zinc-900">Key insights</div>
              <div className="text-xs text-muted-foreground">Pulled from your live procurement data — click to drill down</div>
            </div>
            <ul className="divide-y divide-zinc-100">
              {(data.bullets || []).map((b, i) => (
                <li
                  key={i}
                  onClick={() => goto(b.route)}
                  className={`flex items-start gap-3 px-5 py-3.5 transition-colors ${b.route ? 'cursor-pointer hover:bg-violet-50/50' : ''}`}
                  data-testid={`briefing-bullet-${i}`}
                >
                  <div className="mt-1.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-[10px] font-bold text-white">{i + 1}</div>
                  <p className="flex-1 text-sm text-zinc-800 leading-relaxed">{b.text}</p>
                  {b.route && <ArrowRight className="mt-1 h-4 w-4 shrink-0 text-violet-400" />}
                </li>
              ))}
              {(!data.bullets || data.bullets.length === 0) && (
                <li className="px-5 py-6 text-sm text-muted-foreground">No insights generated.</li>
              )}
            </ul>
          </div>

          <div className="lg:col-span-2 rounded-2xl border border-zinc-200 bg-white shadow-sm overflow-hidden">
            <div className="border-b border-zinc-100 px-5 py-3 bg-gradient-to-r from-rose-50 to-amber-50">
              <div className="text-sm font-semibold text-zinc-900">Recommended actions</div>
              <div className="text-xs text-muted-foreground">Where to spend your attention next</div>
            </div>
            <ul className="divide-y divide-zinc-100">
              {(data.actions || []).map((a, i) => (
                <li
                  key={i}
                  onClick={() => goto(a.route)}
                  className={`px-5 py-3.5 transition-colors ${a.route ? 'cursor-pointer hover:bg-rose-50/50' : ''}`}
                  data-testid={`briefing-action-${i}`}
                >
                  <div className="flex items-center gap-2">
                    <div className="flex-1 text-sm font-semibold text-zinc-900">{a.label}</div>
                    {a.route && <ArrowRight className="h-3.5 w-3.5 text-rose-500" />}
                  </div>
                  {a.why && <p className="mt-1 text-xs text-zinc-600 leading-relaxed">{a.why}</p>}
                </li>
              ))}
              {(!data.actions || data.actions.length === 0) && (
                <li className="px-5 py-6 text-sm text-muted-foreground">No actions recommended.</li>
              )}
            </ul>
          </div>
        </div>
      )}

      <div className="text-center pt-2">
        <p className="text-[10px] text-zinc-400">
          Generated narrative · Always verify before acting on financial decisions
        </p>
      </div>
    </div>
  );
}

function SnapshotTile({ label, value, sub, onClick }) {
  return (
    <div
      onClick={onClick}
      className={`rounded-xl bg-white/12 px-4 py-3 backdrop-blur transition-colors ${onClick ? 'cursor-pointer hover:bg-white/25' : ''}`}
    >
      <div className="text-[10px] font-medium uppercase tracking-wider opacity-75">{label}</div>
      <div className="mt-0.5 text-xl font-bold tabular-nums">{value}</div>
      {sub && <div className="text-[10px] opacity-70">{sub}</div>}
    </div>
  );
}
